/**
 * The subscriber buy box — one picker, two cards, one button.
 *
 * Drives `blocks/ora-sub-buybox.liquid`. A logged-in subscriber chooses
 * between joining an order they already have and buying the product
 * separately, and Horizon's own Add button carries whichever of those is
 * selected: for "join" this element takes the click and posts to Stay's
 * endpoint; for "buy" it writes the selling plan into the product form and
 * lets Horizon do what it always does.
 *
 * Four things worth knowing before changing it:
 *
 * - **It is a custom element.** Horizon re-renders the product form's section
 *   on a variant change, so anything that has to survive a flavor change
 *   belongs in `connectedCallback`, which runs again each time.
 * - **The click is caught in the capture phase**, the pattern
 *   `assets/ora-purchase-options.js` established: Horizon's add button builds
 *   its request from the form at click time, before any submit event exists,
 *   so a submit listener is too late.
 * - **The variant is read late.** Horizon rewrites the form's
 *   `input[name="id"]` when the re-rendered product comes back, so reading it
 *   at the moment it is needed is the only way to be right. `form.id` is
 *   shadowed by that input, so the form is never found by `.id`.
 * - **The add is behind a flag that defaults to off**, because it edits a real
 *   subscription immediately. There is no cart step to undo it and Stay's API
 *   has no remove endpoint — only `fetch` and `add` exist — so an add really
 *   is final. That is also why the design's Undo is not built.
 *
 * **What is Stay's and what is ours.** The two calls are Stay's, unchanged
 * from the script their SE wrote for Ora and Element Ops adapted for the old
 * theme (`snippets/stay-easyadd.liquid` in theme 155050770630, "[LIVE]
 * 09/30/26 [0120] Stay.ai EasyAdd"), so a Stay engineer can debug this
 * against the original line for line:
 *
 * | Original (`stay-easyadd.liquid`)       | Here                          |
 * |----------------------------------------|-------------------------------|
 * | `FETCH_URL`, `ADD_URL`                 | `FETCH_URL`, `ADD_URL` below, same values |
 * | `GET FETCH_URL?email=` + `redirect: 'follow'` | `#fetchSubs()`, the same |
 * | reads `subscriptionId`, `nextBillingDate`, `orderIntervalFrequency`, `orderIntervalUnit`, `lineImageUrls`, `lineItemCount` | `#fetchSubs()`, the same fields |
 * | `parseBillingDate` (strips "22nd")     | `#billingTs()`, the same rule |
 * | sorts by next billing date, soonest first | the same                  |
 * | `POST ADD_URL`, JSON `{subscriptionId, variantId: gid, price, isOneTime}`, `redirect: 'follow'` | `#confirm()`, the same body and options |
 * | variant read from the form's `input[name="id"]` at click time | `#variantId()`, the same |
 * | `DEV_MODE = true` blocks the POST and logs the payload | `live_mode` block setting, off by default, logs the same payload |
 *
 * Two things differ, and both are deliberate:
 *
 * - **The price sent.** The original hardcoded `DISCOUNT = 0.20` for an
 *   "Every order" add and sent the full price for a one-time add. Here the
 *   recurring price is the variant's own Subscribe & Save allocation from
 *   Liquid, so it follows the selling plan if the discount ever changes, and
 *   a one-time add is charged the full price, as in the original, unless the
 *   block's `one_off_discount` setting is turned on (off by default, Erica's
 *   call, 12 Oct 2026). The endpoint charges whatever price it is sent.
 * - **Everything around the two calls is new**: the two-card picker is built
 *   to Ora's SubBox boards rather than the original disclosure, and it drives
 *   Horizon's own Add button instead of a confirm button of its own.
 *
 * Why it could not stay an inline script, see the first bullet above:
 * Horizon throws an inline script away on every flavor change, and the
 * original's 300ms `location.search` poll had nothing to watch here. Its
 * styles also read the old theme's custom properties, none of which exist in
 * Horizon.
 *
 * @typedef {{id: string, apiId: string | number, title: string,
 *            dayLabel: string, shortDate: string, interval: string,
 *            itemCount: number, images: string[], ts: number}} Sub
 */

/**
 * Stay's endpoints, on a Heroku app their SE stood up for Ora rather than in
 * Stay's own product -- so they can move without notice and are not on
 * Stay's status page. They live here rather than in the block's settings
 * (Element Ops' call, 10 Oct 2026): nobody should be editing an API address
 * in the theme editor, and a move is a code change Stay would tell us about.
 */
const FETCH_URL = 'https://duggan-workflows-63ca506469ca.herokuapp.com/api/v1/oraorganic/pdp/fetch';
const ADD_URL = 'https://duggan-workflows-63ca506469ca.herokuapp.com/api/v1/oraorganic/pdp/add';

/**
 * Both of these survive the section re-render that a flavor change causes:
 * Horizon destroys and rebuilds this element each time, and NEITHER the
 * shopper's choices nor their subscriptions depend on which flavor is picked.
 *
 * Without the cache, every flavor change went back to the Heroku dyno for the
 * same list and flashed the skeleton row while it waited -- which is most of
 * why switching flavors felt slow. Horizon's own round trip is about 0.3s,
 * measured on this theme and on the live one, and none of this changes that.
 *
 * Without the choices, a flavor change silently put someone who had chosen
 * "Buy it separately / One-time" back on "Add to your next order" -- the same
 * trap the bundle page's Stay.ai plan default documents.
 */
const choice = { pick: 'join', joinOnce: false, buyOnce: false, subId: null };

/**
 * The element currently driving the page, and the sticky bar's own label as
 * Horizon wrote it.
 *
 * Both are module-level because what we put in the sticky bar lives OUTSIDE
 * this element -- the bar belongs to the section, not to the block -- and a
 * variant change morphs that bar: it keeps the node and our attribute but
 * strips our line out and restores its own label, leaving a wide bar with an
 * empty middle. An instance cannot own state about a node it does not
 * contain, and a label captured after we had already written to it would
 * "restore" to our own text.
 */
let active = null;
let stickyOriginal = null;
let stickyObserver = null;
/**
 * Whether our line is supposed to be in the bar at all. Without this the
 * observer loops forever in the stand-down branch: restoring Horizon's label
 * is itself a mutation inside the observed subtree, made at a moment when our
 * line is deliberately absent, so "line missing -> repaint" calls itself
 * until the main thread starves. Measured: the page stopped responding
 * entirely, which reads as a hung browser rather than as a theme bug.
 */
let stickyWanted = false;
/** A paint is mutating the bar right now; the observer must not re-enter. */
let stickyPainting = false;
/** The variant the bar was last painted for, so a stale paint can be caught. */
let stickyVariant = null;
let variantObserver = null;
let variantRecheck = null;

/**
 * A backstop for the same lag: Horizon may set the input's `value` PROPERTY
 * rather than its attribute, which no observer sees. One bounded recheck
 * after the re-render window costs nothing and cannot loop, because it only
 * repaints when the variant has actually changed.
 */
function recheckVariantSoon(el) {
  clearTimeout(variantRecheck);
  variantRecheck = setTimeout(() => {
    if (!active) return;
    const id = document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value ?? null;
    if (id !== stickyVariant || active.labelStale()) active.refresh();
  }, 1200);
}

/** Put our line back after a morph has taken it. */
function watchStickyBar() {
  if (stickyObserver) return;
  const host = document.querySelector('sticky-add-to-cart');
  if (!host) return;
  stickyObserver = new MutationObserver(() => {
    if (!stickyWanted || stickyPainting || !active) return;
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar || bar.querySelector('.ora-sbb-sticky')) return;
    active.repaintSticky();
  });
  stickyObserver.observe(host, { childList: true, subtree: true });
}

/**
 * Repaint once the chosen flavor has actually settled.
 *
 * Horizon rewrites the form's `input[name="id"]` only when the re-rendered
 * product comes back, about 0.8s after the click -- the same window that used
 * to put the wrong flavor in the cart. The morph that takes our line out of
 * the sticky bar happens inside that window, so the repaint it triggers can
 * read the OLD variant and leave the bar a flavor behind. Measured: clicking
 * Chocolate left "Vanilla / 20" in the bar on one run of two, and nothing
 * repainted after, so it stayed wrong.
 */
function watchVariant() {
  if (variantObserver) return;
  const form = document.querySelector('form[action*="/cart/add"]');
  if (!form) return;
  variantObserver = new MutationObserver(() => {
    if (!active || stickyPainting) return;
    const id = form.querySelector('input[name="id"]')?.value ?? null;
    // The flavor change and the Add button's re-render are two separate
    // mutations: Horizon sets the new id, then morphs the server's button in
    // over ours, which puts "Add to cart" back. Keyed on the id alone, the
    // second one was skipped and the button kept Horizon's label while the
    // picker still said "Add to your next order" -- the add itself still went
    // to the order, but the label promised a cart.
    if (id === stickyVariant && !active.labelStale()) return;
    active.refresh();
  });
  variantObserver.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
}
/** @type {{email: string, subs: Sub[], at: number} | null} */
let subsCache = null;
const SUBS_TTL = 5 * 60 * 1000;

/** Only a floor, for a variant whose plan price never reached the markup. */
const FALLBACK_DISCOUNT = 0.2;

class OraSubBuybox extends HTMLElement {
  /** @type {Sub[]} */
  #subs = [];
  /** @type {number | null} */
  #selected = null;
  /** 'join' | 'buy' */
  #pick = 'join';
  /** On the join card: every order, or just the next one. */
  #joinOnce = false;
  /** On the buy card: a new subscription, or a one-off. */
  #buyOnce = false;
  #done = false;
  /**
   * Stepped aside for a sold-out or pre-order flavor -- see `#syncShelf`.
   * Unlike `#standDown` this is undone by picking an in-stock flavor.
   */
  #parked = false;
  /** Horizon's own button label, kept so standing down can put it back. */
  #original;
  /** The label this element last wrote, so ours can be told from Horizon's. */
  #written;
  /**
   * The footnote, held by reference because `#placeFoot` moves it out of this
   * element to sit under the Add button -- after which `#el('foot')`, which
   * searches this element's descendants, finds nothing.
   */
  #foot = null;
  /** Our line in the sticky bar, re-found rather than owned -- see `active`. */
  #sticky = null;
  /** @type {Record<string, {price: number, sub: number | null, title: string}>} */
  #prices = {};

  connectedCallback() {
    // Before the init guard: a morph can detach and reattach this very node,
    // which fires both callbacks again but leaves `oraReady` set, and an
    // element that returns here without claiming `active` leaves the observer
    // with nobody to call.
    active = this;
    if (this.dataset.oraReady) return;
    this.dataset.oraReady = '1';

    this.email = this.dataset.email || '';
    this.subPageBase = this.dataset.subPageBase || '/apps/retextion/shop/subscriptions/';
    this.liveMode = this.dataset.liveMode === 'true';
    // Full price for a one-off unless the block says otherwise: a missing
    // attribute must not hand out a discount.
    this.oneOffDiscount = this.dataset.oneOffDiscount === 'true';
    this.productName = this.dataset.productName || '';
    this.fallbackVariant = this.dataset.fallbackVariant || '';
    this.fallbackPrice = parseFloat(this.dataset.fallbackPrice || '0');
    this.fallbackSubPrice = parseFloat(this.dataset.fallbackSubPrice || '0') || null;

    try {
      const node = this.querySelector('[data-sbb-variants]');
      if (node) {
        for (const v of JSON.parse(node.textContent).variants) {
          this.#prices[v.id] = {
            price: v.price,
            sub: typeof v.sub === 'number' ? v.sub : null,
            title: v.title || '',
          };
        }
      }
    } catch (err) {
      console.error('[ora-sub-buybox] variant prices did not parse', err);
    }

    document.addEventListener('click', this.#onDocClick, true);
    document.addEventListener('submit', this.#onDocSubmit, true);

    active = this;
    // Here as well as in the sticky paint, which returns early on a page
    // without the bar: a flavor change is what brings this back from parked.
    watchVariant();
    this.#wire();
    this.#restoreChoice();
    this.#placeFoot();
    this.#hideStay();
    this.#paint();

    if (subsCache && subsCache.email === this.email && Date.now() - subsCache.at < SUBS_TTL) {
      this.#subs = subsCache.subs;
      this.#renderSubs();
      this.#paint();
      return;
    }

    this.#fetchSubs().then(
      (subs) => {
        this.#subs = subs;
        subsCache = { email: this.email, subs, at: Date.now() };
        if (!subs.length) {
          // Nothing to join. The picker has no first card worth showing, so
          // the ordinary buy box is the honest thing to leave them with.
          this.#standDown();
          return;
        }
        this.#renderSubs();
        this.#paint();
      },
      (err) => {
        console.error('[ora-sub-buybox] could not load subscriptions', err);
        this.#standDown();
      }
    );
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick, true);
    document.removeEventListener('submit', this.#onDocSubmit, true);
    // Deferred, because a section re-render can connect the replacement
    // before disconnecting us; nulling synchronously would then throw away
    // the live element's claim.
    if (active === this) {
      queueMicrotask(() => {
        if (active === this && !this.isConnected) active = null;
      });
    }
  }

  /** Called by the observer above; the paint itself is private. */
  repaintSticky() {
    if (this.#syncShelf()) return;
    this.#paintSticky();
  }

  /**
   * A flavor change: everything follows the new variant -- the prices on both
   * cards, the Add button's label and the sticky bar -- not just the bar.
   */
  refresh() {
    this.#paint();
  }

  /**
   * Whether the Add button no longer says what this element last wrote,
   * because something else (Horizon's re-render on a flavor change) has
   * written over it. Our own write changes the label too, which is why this
   * compares against `#written` rather than reacting to any change -- that
   * would repaint in a loop.
   */
  labelStale() {
    if (this.#parked || this.#written === undefined) return false;
    const button = this.#addButton();
    const label = button && (button.querySelector('.add-to-cart-text') || button);
    return !!label && label.textContent !== this.#written;
  }

  #el(name) {
    return this.querySelector(`[data-sbb="${name}"]`);
  }

  /** Put back what the shopper chose before the re-render threw it away. */
  #restoreChoice() {
    this.#pick = choice.pick;
    this.#joinOnce = choice.joinOnce;
    this.#buyOnce = choice.buyOnce;
    const radio = this.#el(this.#pick === 'buy' ? 'pick-buy' : 'pick-join');
    if (radio instanceof HTMLInputElement) radio.checked = true;
    this.#el('mode-every')?.setAttribute('aria-pressed', String(!this.#joinOnce));
    this.#el('mode-once')?.setAttribute('aria-pressed', String(this.#joinOnce));
    this.#el('buy-sub')?.setAttribute('aria-pressed', String(!this.#buyOnce));
    this.#el('buy-once')?.setAttribute('aria-pressed', String(this.#buyOnce));
  }

  #wire() {
    this.#el('pick-join')?.addEventListener('change', () => this.#setPick('join'));
    this.#el('pick-buy')?.addEventListener('change', () => this.#setPick('buy'));
    this.#el('mode-every')?.addEventListener('click', () => this.#setJoinMode(false));
    this.#el('mode-once')?.addEventListener('click', () => this.#setJoinMode(true));
    this.#el('buy-sub')?.addEventListener('click', () => this.#setBuyMode(false));
    this.#el('buy-once')?.addEventListener('click', () => this.#setBuyMode(true));
    // Picking a frequency is a vote for subscribing, and for this card.
    this.#el('plan')?.addEventListener('change', () => {
      this.#setPick('buy', true);
      this.#setBuyMode(false);
    });
  }

  /* ---- the rest of the buy box ------------------------------------------ */

  /**
   * Stay's cards say the same thing this picker now says, so they go — but
   * only on the pages this draws on, and only once the script is running, so
   * a failed script leaves a working buy box rather than none.
   */
  #hideStay() {
    const block = this.#stayBlock();
    if (block instanceof HTMLElement) {
      block.style.display = 'none';
      block.setAttribute('data-ora-sbb-hidden', '');
    }
  }

  #stayBlock() {
    const column = this.parentElement;
    if (!column) return null;
    for (const child of column.children) {
      if (child === this) continue;
      const id = child.id || '';
      if (/stay/i.test(id) || child.querySelector?.('.stayai-subscriptions, [class^="stayai-"]')) return child;
    }
    return null;
  }

  /** Horizon's own add button, which this element speaks through. */
  #addButton() {
    return this.#form()?.querySelector('button[name="add"]') ?? null;
  }

  #form() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  /**
   * The footnote belongs under the Add button, which is a sibling block
   * further down the column rather than a child of this one, so it is
   * rendered here and moved there.
   */
  #placeFoot() {
    const foot = this.#el('foot');
    this.#foot = foot;
    const button = this.#addButton();
    const host = button?.closest('.buy-buttons-block') || button?.closest('[class*="buy-buttons"]');
    // Inside, not after: `.ora-trust` pulls itself up by the whole column gap
    // (`calc(var(--ora-trust-space) - var(--gap))`), so a sibling standing
    // between the button and the arrival row is painted over by it.
    if (foot && host) host.append(foot);
  }

  /**
   * The arrival estimate is the shipping window for a NEW order, which is not
   * what happens when the line rides an order already scheduled -- it arrives
   * with that one, on its own date, which the card above already states. So
   * the row goes while "add to your next order" is the selection, and comes
   * back the moment it is not. The whole block is hidden rather than its
   * contents, because an empty flex child still takes the column's gap.
   *
   * Only the arrival row: the guarantee and the HSA line are true either way.
   */
  #paintEta() {
    const eta = document.querySelector('.ora-eta');
    const host = eta?.closest('.shopify-block') || eta;
    if (!(host instanceof HTMLElement)) return;
    const hide = !this.#parked && this.#pick === 'join';
    host.style.display = hide ? 'none' : '';
    host.toggleAttribute('data-ora-sbb-hidden-eta', hide);
  }

  /**
   * The sticky bar mirrors the selection, so scrolling past the buy box never
   * quietly swaps a subscriber onto a second subscription -- which is what it
   * did, since it is Horizon's ordinary add-to-cart.
   *
   * Nothing of Horizon's is rewritten: our own line is added and its own
   * title, variant and price are hidden by an attribute on the bar, so taking
   * the attribute off puts the bar back exactly as it was. Hiding its price
   * also keeps us out of the way of `ora-quick-add.js`, which writes the
   * checked Stay option's price in there -- and Stay's block is hidden here.
   */
  #paintSticky() {
    if (stickyPainting) return;
    stickyPainting = true;
    try {
      this.#paintStickyInner();
    } finally {
      // After the writes, so the observer sees the finished state.
      queueMicrotask(() => {
        stickyPainting = false;
      });
    }
  }

  #paintStickyInner() {
    const bar = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar');
    if (!bar) return;
    const sub = this.#sub();
    const mine = !this.#parked && this.#pick === 'join' && !!sub;

    watchStickyBar();
    watchVariant();
    stickyVariant = this.#form()?.querySelector('input[name="id"]')?.value ?? null;
    recheckVariantSoon();

    const button = bar.querySelector('.sticky-add-to-cart__button');
    const label = button?.querySelector('.add-to-cart-text__content > span');
    // Captured once, and only from a label we have not written to -- after a
    // morph this is Horizon's own text again, which is what we want back.
    if (label && stickyOriginal === null) stickyOriginal = label.textContent;

    if (!mine) {
      stickyWanted = false;
      bar.removeAttribute('data-ora-sbb');
      bar.querySelector('.ora-sbb-sticky')?.remove();
      this.#sticky = null;
      if (label && stickyOriginal !== null) label.textContent = stickyOriginal;
      return;
    }

    stickyWanted = true;
    bar.setAttribute('data-ora-sbb', '');

    // Adopt whatever is already in the bar rather than adding beside it: a
    // flavor change builds a new element, and without this each one left the
    // last one's line behind -- measured 1, then 2, then 3.
    this.#sticky = bar.querySelector('.ora-sbb-sticky');

    if (!this.#sticky) {
      const wrap = document.createElement('div');
      wrap.className = 'ora-sbb-sticky';

      const top = document.createElement('span');
      top.className = 'ora-sbb-sticky__top';

      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'ora-sbb-sticky__change';
      change.textContent = 'Change';
      // `scrollIntoView` rather than a fragment link, because above 990px the
      // page does not scroll -- `.page-wrapper` does -- and this walks up to
      // whichever ancestor is the real scrollport.
      // Resolved at click time, not closed over: this node outlives the
      // instance that made it, and a detached element scrolls nothing.
      change.addEventListener('click', () =>
        document.querySelector('ora-sub-buybox')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      );

      const note = document.createElement('span');
      note.className = 'ora-sbb-sticky__note';

      // Change rides the note's line, not the price's: the price line is the
      // tighter of the two once the note is shortened, and "Vanilla · $47.99"
      // has no ellipsis worth reading.
      const row = document.createElement('span');
      row.className = 'ora-sbb-sticky__row';
      row.append(note, change);
      wrap.append(top, row);
      bar.insertBefore(wrap, button ?? null);
      this.#sticky = wrap;
    }

    const variant = this.#variantTitle();
    this.#sticky.querySelector('.ora-sbb-sticky__top').textContent = [variant, this.#money(this.#joinPrice())]
      .filter(Boolean)
      .join('  ·  ');
    // On a phone the bar leaves this column about 200px, where the long
    // version truncates mid-word -- and its first half only repeats the date
    // the button beside it already carries. What is worth the room is the
    // part the button does NOT say.
    const roomy = this.#sticky.clientWidth >= 260;
    this.#sticky.querySelector('.ora-sbb-sticky__note').textContent = roomy
      ? `Into your ${sub.shortDate} order  ·  no extra shipment`
      : 'No extra shipment';
    if (label) label.textContent = this.#done ? `Added to ${sub.shortDate} order` : `Add to ${sub.shortDate} order`;
  }

  /**
   * The selected flavor's entry in the product form's `data-ora-variants`
   * map, when it is sold out or on pre-order -- otherwise null.
   *
   * blocks/buy-buttons.liquid decides both (a sold-out entry carries
   * `soldout`, a pre-order entry its own selling plan; single serves are in
   * the map too and are left alone, since this never draws for them). Neither
   * flavor can ride an order already on its way: a sold-out one has nothing
   * to send, and a pre-order has to go through its own plan and ship date. So
   * on those flavors this steps aside and the buy box is the ordinary one --
   * the back-in-stock sign-up, or the Pre-Order button.
   */
  #offShelf() {
    const holder = this.#form()?.closest('product-form-component');
    const text = holder instanceof HTMLElement ? holder.dataset.oraVariants : '';
    if (!text) return null;
    try {
      const entry = JSON.parse(text)[String(this.#variantId())];
      return entry && !entry.single ? entry : null;
    } catch {
      return null;
    }
  }

  /**
   * Park or unpark for the selected flavor. Returns true while parked, so a
   * caller about to paint knows not to.
   */
  #syncShelf() {
    const entry = this.#offShelf();
    const off = Boolean(entry);
    if (off === this.#parked) return off;
    this.#parked = off;
    if (off) this.#park(entry);
    else this.#unpark();
    return off;
  }

  /** @param {{soldout?: boolean}} entry */
  #park(entry) {
    this.style.display = 'none';
    const block = this.#stayBlock();
    if (block instanceof HTMLElement && block.hasAttribute('data-ora-sbb-hidden')) {
      // Stay's block comes back as far as this element is concerned; whether
      // it shows is ora-buy-box-variants.js's call, and it hides it for both.
      block.style.removeProperty('display');
      block.removeAttribute('data-ora-sbb-hidden');
    }
    const button = this.#addButton();
    if (button) {
      const label = button.querySelector('.add-to-cart-text') || button;
      if (this.#written !== undefined && label.textContent === this.#written && this.#original !== undefined) {
        label.textContent = this.#original;
      }
      // A sold-out flavor's button stays dead under the sign-up; a pre-order
      // one has to take the add.
      button.disabled = Boolean(entry.soldout);
    }
    if (this.#foot) this.#foot.hidden = true;
    this.#form()?.querySelector('input[name="selling_plan"][data-ora-sbb]')?.remove();
    this.#paintEta();
    this.#paintSticky();
  }

  #unpark() {
    this.style.removeProperty('display');
    this.#hideStay();
    this.#paint();
  }

  /** Give the page back to Horizon and Stay, and take this away. */
  #standDown() {
    const block = this.#stayBlock();
    if (block instanceof HTMLElement && block.hasAttribute('data-ora-sbb-hidden')) {
      block.style.removeProperty('display');
      block.removeAttribute('data-ora-sbb-hidden');
    }
    this.#restoreButton();
    stickyWanted = false;
    document.querySelector('[data-ora-sbb]')?.removeAttribute('data-ora-sbb');
    this.#sticky?.remove();
    const stickyLabel = document.querySelector(
      '.sticky-add-to-cart__button .add-to-cart-text__content > span'
    );
    if (stickyLabel && stickyOriginal !== null) stickyLabel.textContent = stickyOriginal;
    const eta = document.querySelector('[data-ora-sbb-hidden-eta]');
    if (eta instanceof HTMLElement) {
      eta.style.removeProperty('display');
      eta.removeAttribute('data-ora-sbb-hidden-eta');
    }
    this.#foot?.remove();
    this.remove();
  }

  /* ---- price ------------------------------------------------------------ */

  #variantId() {
    // `form.id` is shadowed by the `<input name="id">` inside it, so this has
    // to go through querySelector rather than reading a property off the form.
    const input = this.#form()?.querySelector('input[name="id"]');
    if (input?.value) return parseInt(input.value, 10);
    const param = new URLSearchParams(location.search).get('variant');
    return param ? parseInt(param, 10) : parseInt(this.fallbackVariant, 10);
  }

  #basePrice() {
    return this.#prices[this.#variantId()]?.price ?? this.fallbackPrice;
  }

  /**
   * What this variant costs on the subscription plan, per delivery.
   *
   * The two fallbacks are a PAIR, for a variant we were not given: using the
   * fallback plan price beside a known variant's own base price quotes one
   * flavor's subscription price against another's full price.
   */
  #subPrice() {
    const entry = this.#prices[this.#variantId()];
    if (entry) {
      if (typeof entry.sub === 'number' && entry.sub > 0) return entry.sub;
      return Math.round(entry.price * (1 - FALLBACK_DISCOUNT) * 100) / 100;
    }
    if (this.fallbackSubPrice > 0) return this.fallbackSubPrice;
    return Math.round(this.fallbackPrice * (1 - FALLBACK_DISCOUNT) * 100) / 100;
  }

  /**
   * What joining an order costs. Adding to every order is always the plan's
   * price; adding to just one is a merchandising call, so it is a setting --
   * and it matters more than it looks, because the endpoint is told whatever
   * price is shown here and charges it.
   */
  #joinPrice() {
    if (this.#joinOnce && !this.oneOffDiscount) return this.#basePrice();
    return this.#subPrice();
  }

  /**
   * The WHOLE variant title, pack size and all -- "Vanilla Chai / 20", not
   * "Vanilla Chai". The SubBox board shows only the flavor; keeping the size
   * is Erica's call, 5 Oct 2026, because the sticky bar is a place someone
   * may act from without scrolling back to check which pack they are on.
   * Do not trim it back to match the board.
   */
  #variantTitle() {
    return this.#prices[this.#variantId()]?.title || '';
  }

  #money(n) {
    return `$${n.toFixed(2)}`;
  }

  /* ---- selection -------------------------------------------------------- */

  #setPick(pick, silent) {
    this.#pick = pick === 'buy' ? 'buy' : 'join';
    choice.pick = this.#pick;
    if (silent) {
      const radio = this.#el(this.#pick === 'buy' ? 'pick-buy' : 'pick-join');
      if (radio instanceof HTMLInputElement) radio.checked = true;
    }
    this.#showError(false);
    this.#paint();
  }

  #setJoinMode(once) {
    this.#joinOnce = once;
    choice.joinOnce = once;
    this.#el('mode-every')?.setAttribute('aria-pressed', String(!once));
    this.#el('mode-once')?.setAttribute('aria-pressed', String(once));
    this.#paint();
  }

  #setBuyMode(once) {
    this.#buyOnce = once;
    choice.buyOnce = once;
    this.#el('buy-sub')?.setAttribute('aria-pressed', String(!once));
    this.#el('buy-once')?.setAttribute('aria-pressed', String(once));
    this.#setPick('buy', true);
    this.#paint();
  }

  #sub() {
    return this.#selected === null ? this.#subs[0] : this.#subs[this.#selected];
  }

  /* ---- painting --------------------------------------------------------- */

  #paint() {
    if (this.#syncShelf()) return;
    const base = this.#basePrice();
    const plan = this.#subPrice();
    const sub = this.#sub();

    const join = this.#joinPrice();
    this.#text('join-now', this.#money(join));
    const joinWas = this.#el('join-was');
    if (joinWas) {
      joinWas.hidden = join >= base;
      joinWas.textContent = this.#money(base);
    }

    // Card two quotes whichever side of its toggle is showing. Closed, it is
    // "from <the cheaper of the two>", because both live behind it.
    const buyPrice = this.#buyOnce ? base : plan;
    this.#text('buy-now', this.#money(this.#pick === 'buy' ? buyPrice : plan));
    const buyWas = this.#el('buy-was');
    if (buyWas) {
      const show = this.#pick === 'buy' && !this.#buyOnce && plan < base;
      buyWas.hidden = !show;
      buyWas.textContent = this.#money(base);
    }
    const from = this.#el('buy-from');
    if (from) from.hidden = this.#pick === 'buy';

    // Card two's second line, and its frequency control, follow the toggle.
    this.#text(
      'buy-note',
      this.#pick !== 'buy'
        ? 'A new subscription or a one-time order.'
        : this.#buyOnce
          ? 'A single order that ships on its own.'
          : 'Ships separately, on its own schedule.'
    );
    const freq = this.#el('freq');
    if (freq) freq.hidden = this.#buyOnce;
    const benefits = this.#el('benefits');
    if (benefits) benefits.hidden = this.#buyOnce;

    // Card one's second line names the date when there is only one order to
    // join, and stays general when the reader still has to choose.
    // With one subscription the row right below names the date, so saying it
    // here first only says it twice. With several there is no single date
    // above yet, so the line carries that much.
    this.#text(
      'join-note',
      sub && this.#subs.length > 1
        ? 'Ships with a subscription you already have. No extra shipment, no new subscription.'
        : 'No extra shipment, no new subscription.'
    );

    this.#paintManage();
    this.#paintEta();
    this.#paintButton();
    this.#paintSticky();

    // Nothing submits the form while "join" is selected -- both the click and
    // the submit are stopped -- but leaving our plan sitting in it is the same
    // shape of trap Stay's lingering input just sprang, so it goes.
    if (this.#pick === 'join') {
      this.#form()?.querySelector('input[name="selling_plan"][data-ora-sbb]')?.remove();
    }
  }

  #text(name, value) {
    const el = this.#el(name);
    if (el) el.textContent = value;
  }

  #paintManage() {
    const link = this.#el('manage');
    const sub = this.#sub();
    if (!link) return;
    link.hidden = !sub;
    if (sub) link.href = this.subPageBase + sub.id;
  }

  /**
   * The button says what will happen, which is the whole point of there
   * being one button: "Add to Oct 22 order" and "Add to cart · $59.99" are
   * different promises and a shopper should not have to infer which is live.
   */
  #paintButton() {
    const button = this.#addButton();
    if (!button) return;
    const label = button.querySelector('.add-to-cart-text') || button;
    // Re-read whenever the label is not ours: a flavor change has Horizon
    // write its own text back ("Pre-Order", "Add to cart"), and that is what
    // stepping aside should return to.
    if (this.#original === undefined || label.textContent !== this.#written) this.#original = label.textContent;

    const sub = this.#sub();
    // "Done" belongs to the join card alone. A shopper who has just added to
    // their order and then picks "Buy it separately" is asking for a second,
    // ordinary add, and the button has to come back for it -- keyed on `#done`
    // alone it stayed disabled and read "Added to ... order" over the other
    // card (found by Element Ops on staging, 10 Oct 2026).
    const joined = this.#done && this.#pick === 'join';
    let text;
    if (joined) text = sub ? `Added to ${sub.shortDate} order` : 'Added';
    else if (this.#pick === 'join') text = sub ? `Add to ${sub.shortDate} order` : 'Add to your next order';
    else if (this.#buyOnce) text = `Add to cart · ${this.#money(this.#basePrice())}`;
    else text = `Subscribe · ${this.#money(this.#subPrice())}`;

    label.textContent = text;
    this.#written = text;
    button.disabled = joined || (this.#pick === 'join' && !sub);

    const foot = this.#foot;
    if (foot) {
      const show = this.#pick === 'join' && !!sub && !this.#done;
      foot.hidden = !show;
      if (show) foot.textContent = 'Charged with that order. Change it anytime before it ships.';
    }
  }

  #restoreButton() {
    const button = this.#addButton();
    if (!button || this.#original === undefined) return;
    const label = button.querySelector('.add-to-cart-text') || button;
    label.textContent = this.#original;
    button.disabled = false;
  }

  #showError(show, message) {
    const el = this.#el('error');
    if (!el) return;
    el.hidden = !show;
    if (show && message) el.textContent = message;
  }

  /* ---- subscriptions ----------------------------------------------------- */

  async #fetchSubs() {
    const res = await fetch(`${FETCH_URL}?email=${encodeURIComponent(this.email)}`, { redirect: 'follow' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const payload = await res.json();
    const rows = Array.isArray(payload) ? payload : payload?.data || [];

    return rows
      .map((sub) => {
        const images = Array.isArray(sub.lineImageUrls) ? sub.lineImageUrls : [];
        const ts = this.#billingTs(sub.nextBillingDate);
        const count = typeof sub.lineItemCount === 'number' ? sub.lineItemCount : images.length;
        return {
          id: String(sub.subscriptionId),
          // Sent back exactly as Stay returned it, as the original did --
          // `id` is the string form, for comparing and for URLs.
          apiId: sub.subscriptionId,
          dayLabel: this.#dayLabel(ts, sub.nextBillingDate),
          shortDate: this.#shortDate(ts, sub.nextBillingDate),
          interval: this.#interval(sub.orderIntervalFrequency, sub.orderIntervalUnit),
          itemCount: count,
          images,
          ts,
        };
      })
      .sort((a, b) => a.ts - b.ts);
  }

  /** The API returns human dates ("October 22nd, 2026"), which Date rejects. */
  #billingTs(str) {
    if (!str) return Infinity;
    const direct = new Date(str).getTime();
    if (!Number.isNaN(direct)) return direct;
    const stripped = new Date(String(str).replace(/(\d+)(st|nd|rd|th)/gi, '$1')).getTime();
    return Number.isNaN(stripped) ? Infinity : stripped;
  }

  /** "Thu, Oct 22" — the weekday is what makes a date feel near. */
  #dayLabel(ts, raw) {
    if (!Number.isFinite(ts)) return String(raw || '');
    return new Date(ts).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  /** "Oct 22", for the button, where the weekday would not earn its room. */
  #shortDate(ts, raw) {
    if (!Number.isFinite(ts)) return String(raw || '');
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  #interval(frequency, unit) {
    if (!frequency || !unit) return '';
    const word = String(unit).toLowerCase();
    return `Every ${frequency} ${word}${frequency > 1 ? 's' : ''}`;
  }

  #renderSubs() {
    const list = this.#el('list');
    if (!list) return;
    list.textContent = '';

    const many = this.#subs.length > 1;
    const label = this.#el('list-label');
    if (label) label.hidden = !many;
    const modes = this.#el('modes');
    if (modes) modes.hidden = false;

    this.#subs.forEach((sub, idx) => {
      // One subscription is not a choice, so it is stated rather than offered.
      const row = document.createElement(many ? 'button' : 'span');
      if (row instanceof HTMLButtonElement) {
        row.type = 'button';
        row.addEventListener('click', () => this.#select(idx));
      }
      row.className = `ora-sbb__row${many ? '' : ' ora-sbb__row--only'}`;
      row.dataset.sbbRow = String(idx);

      const text = document.createElement('span');
      text.className = 'ora-sbb__row-text';
      const name = document.createElement('span');
      name.className = 'ora-sbb__row-name';
      name.textContent = many ? sub.dayLabel : `${sub.dayLabel} order`;
      const meta = document.createElement('span');
      meta.className = 'ora-sbb__row-meta';
      meta.textContent = [sub.interval, `${sub.itemCount} item${sub.itemCount === 1 ? '' : 's'}`]
        .filter(Boolean)
        .join('  ·  ');
      text.append(name, meta);

      row.append(text, this.#thumbs(sub));
      list.append(row);
    });

    // By id, not index: an index would point at a different subscription if
    // the list ever came back in another order.
    const kept = choice.subId ? this.#subs.findIndex((x) => x.id === choice.subId) : -1;
    this.#select(kept >= 0 ? kept : 0);
  }

  #thumbs(sub) {
    const wrap = document.createElement('span');
    wrap.className = 'ora-sbb__thumbs';
    // Three photos where the column has room for them, two where it does not.
    const max = this.clientWidth && this.clientWidth < 380 ? 2 : 3;
    for (const url of sub.images.slice(0, max)) {
      const img = document.createElement('img');
      img.src = `${url}${url.includes('?') ? '&' : '?'}width=60`;
      img.alt = '';
      img.loading = 'lazy';
      img.className = 'ora-sbb__thumb';
      img.addEventListener('error', () => (img.hidden = true));
      wrap.append(img);
    }
    const rest = sub.itemCount - max;
    if (rest > 0) {
      const chip = document.createElement('span');
      chip.className = 'ora-sbb__thumb ora-sbb__thumb--more';
      chip.textContent = `+${rest}`;
      wrap.append(chip);
    }
    return wrap;
  }

  #select(idx) {
    for (const row of this.querySelectorAll('[data-sbb-row]')) {
      row.classList.toggle('is-selected', Number(row.dataset.sbbRow) === idx);
    }
    this.#selected = idx;
    choice.subId = this.#subs[idx]?.id ?? null;
    this.#showError(false);
    this.#paint();
  }

  /* ---- the two submit paths ---------------------------------------------- */

  /* Bound fields, not methods: a private method cannot be reassigned, so
     `this.#x = this.#x.bind(this)` throws, and an unbound listener loses
     `this` and cannot be removed again. */
  #onDocClick = (event) => {
    if (this.#parked) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const sticky = target.closest('.sticky-add-to-cart__button');
    const button = target.closest('button[name="add"]') || sticky;
    const form = this.#form();
    // The sticky button is not in the form -- it carries Horizon's own click
    // handler instead -- so it is matched on its class rather than its form.
    if (!(button instanceof HTMLButtonElement)) return;
    if (!sticky && (!form || button.form !== form)) return;

    if (this.#pick === 'join') {
      // Horizon builds its cart request from the form at click time, so this
      // has to stop here rather than at submit.
      event.preventDefault();
      event.stopPropagation();
      this.#confirm();
      return;
    }
    if (form) this.#syncPlan(form);
  };

  #onDocSubmit = (event) => {
    if (this.#parked) return;
    const form = this.#form();
    if (!form || event.target !== form) return;
    if (this.#pick === 'join') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#syncPlan(form);
  };

  /**
   * The selling plan goes in an input INSIDE the form rather than one
   * associated by a `form` attribute, because buy-buttons clears other plans
   * by querying the form's descendants and an input outside it would slip
   * past that.
   */
  #syncPlan(form) {
    const preorder = form.querySelector('input[name="selling_plan"][data-ora-preorder]');

    // Stay's block is HIDDEN, not removed, so its script is still running and
    // still writes its own selling_plan straight into the form -- not inside
    // its own block, where ignoring it would have been enough. Removing only
    // ours on a one-time therefore left Stay's behind and every one-time add
    // went to checkout as a subscription. Anything that is neither ours nor
    // the pre-order input goes, on both paths: two selling_plan fields in one
    // form is ambiguous even when we do want one.
    for (const node of form.querySelectorAll('[name="selling_plan"]')) {
      if (node === preorder || node.hasAttribute('data-ora-sbb')) continue;
      node.remove();
    }

    let ours = form.querySelector('input[name="selling_plan"][data-ora-sbb]');

    if (preorder || this.#buyOnce) {
      ours?.remove();
      return;
    }

    const select = this.#el('plan');
    if (!(select instanceof HTMLSelectElement) || !select.value) return;

    if (!(ours instanceof HTMLInputElement)) {
      ours = document.createElement('input');
      ours.type = 'hidden';
      ours.name = 'selling_plan';
      ours.setAttribute('data-ora-sbb', '');
      form.appendChild(ours);
    }
    ours.value = select.value;
  }

  async #confirm() {
    const sub = this.#sub();
    const variantId = this.#variantId();
    if (!sub || !variantId || this.#done) return;

    const payload = {
      subscriptionId: sub.apiId,
      variantId: `gid://shopify/ProductVariant/${variantId}`,
      // NOTE: the endpoint takes the price from the browser. That is their API
      // contract, not a choice made here, and it is raised with Stay --
      // anything that can run JS on this page can name its own price.
      price: this.#joinPrice(),
      isOneTime: this.#joinOnce,
    };

    if (!this.liveMode) {
      console.log('[ora-sub-buybox] live mode off, nothing sent. Payload would be:', payload);
      this.#succeed(sub);
      return;
    }

    const button = this.#addButton();
    if (button) button.disabled = true;
    try {
      const res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        redirect: 'follow',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.#succeed(sub);
    } catch (err) {
      console.error('[ora-sub-buybox] add failed', err);
      this.#showError(true, 'We could not add it to that order. Please try again.');
      if (button) button.disabled = false;
    }
  }

  /**
   * The card becomes a receipt. There is no Undo: Stay's API has `fetch` and
   * `add` and nothing else, so the honest offer is the portal, where the line
   * can be removed before the order ships.
   */
  #succeed(sub) {
    this.#done = true;
    const card = this.querySelector('.ora-sbb__card--join');
    if (!card) return;

    const variant = this.#variantTitle();
    const receipt = document.createElement('span');
    receipt.className = 'ora-sbb__receipt';

    const head = document.createElement('span');
    head.className = 'ora-sbb__receipt-head';
    head.textContent = `Added to your ${sub.shortDate} order`;

    const what = document.createElement('span');
    what.className = 'ora-sbb__receipt-what';
    what.textContent = [this.productName, variant].filter(Boolean).join('  ·  ');

    const when = document.createElement('span');
    when.className = 'ora-sbb__receipt-when';
    when.textContent = `${this.#joinOnce ? 'Just this order' : 'Every order'}  ·  ships ${sub.dayLabel}`;

    const price = document.createElement('span');
    price.className = 'ora-sbb__receipt-price';
    price.textContent = this.#money(this.#joinPrice());

    const note = document.createElement('span');
    note.className = 'ora-sbb__receipt-note';
    note.textContent = `You'll be charged ${this.#money(this.#joinPrice())} when the order ships.`;

    const link = document.createElement('a');
    link.className = 'ora-sbb__receipt-link';
    link.href = this.subPageBase + sub.id;
    link.textContent = 'View this order';

    receipt.append(head, what, when, price, note, link);
    card.replaceChildren(receipt);
    card.classList.add('is-done');

    this.#paintButton();
  }
}

if (!customElements.get('ora-sub-buybox')) {
  customElements.define('ora-sub-buybox', OraSubBuybox);
}
